import dayjs from 'dayjs';
import {
  Table,
  Typography,
  Button,
  Dropdown,
  Modal,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  DownloadOutlined,
  SyncOutlined,
  CloudUploadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  MinusCircleOutlined,
  HistoryOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import {
  useGetKeyenceProgramsByProjectQuery,
  useSyncKeyenceProgramMutation,
  useSyncFromKeyenceMutation,
  usePushKeyenceProgramToDeviceMutation,
} from '../../../../redux/api/quality';
import { KeyenceProgram, KeyenceValidationSummary } from '../../../../interfaces/Quality.Interface';
import { useMemo, useState } from 'react';
import UploadValidationResults from '../KeyenceFileUpload/UploadValidationResults';

const { Text, Title } = Typography;

// Below this width the table scrolls inside its card instead of widening the page.
const TABLE_MIN_WIDTH = 1250;

const HISTORY_COLUMN_WIDTHS: Record<string, number> = {
  view: 90,
  version: 90,
  uploaded_by_name: 160,
  device_sync_status: 120,
  validation: 140,
  created_time: 180,
  action: 120,
};
const HISTORY_TABLE_MIN_WIDTH = Object.values(HISTORY_COLUMN_WIDTHS).reduce((sum, w) => sum + w, 0);

const VALIDATION_INDICATOR: Record<
  KeyenceValidationSummary['outcome'],
  { label: string; color: string; icon: JSX.Element }
> = {
  Passed: { label: 'Passed', color: '#52c41a', icon: <CheckCircleFilled /> },
  Warning: { label: 'Warnings', color: '#d48806', icon: <ExclamationCircleFilled /> },
  Failed: { label: 'Failed', color: '#ff4d4f', icon: <CloseCircleFilled /> },
  Pending: { label: 'Checking', color: '#1677ff', icon: <SyncOutlined spin /> },
  Skipped: { label: 'Not checked', color: 'rgba(0, 0, 0, 0.45)', icon: <MinusCircleOutlined /> },
  'Parse Error': { label: 'Unreadable', color: '#fa541c', icon: <ExclamationCircleFilled /> },
};

const validationTooltip = (validation: KeyenceValidationSummary) => {
  if (validation.outcome === 'Pending') return 'Large program — being checked in the background';
  if (validation.outcome === 'Skipped' || validation.outcome === 'Parse Error') {
    return validation.parse_error ?? undefined;
  }
  const parts = [
    `${validation.matched_count} of ${validation.plan_balloon_count} plan balloons matched`,
  ];
  if (validation.error_count) parts.push(`${validation.error_count} error(s)`);
  if (validation.warning_count) parts.push(`${validation.warning_count} warning(s)`);
  return parts.join(' · ');
};

const KeyencePrograms = ({
  projectId
}: {
  projectId: string
}) => {

  const { data: programsData, isLoading: isLoading, refetch } = useGetKeyenceProgramsByProjectQuery(
    { projectId },
    {
      skip: !projectId,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true
    },
  );

  // Group programs by file_name and view to handle versioning
  const groupedData = useMemo(() => {
    if (!programsData) return [];

    const groups: Record<string, KeyenceProgram[]> = {};

    programsData.forEach((program) => {
      // Create a unique key for grouping (Filename + View)
      // The requirement says "grouped by file name", but usually View + Filename is the unique constraint.
      // Assuming Filename is unique enough or we want to group purely by filename.
      // Let's group by `view` + `file_name` to be safe, or just `file_name` if they are truly unique across views.
      // Given the upload logic checks (plan, view), it seems files are unique per view.
      // If we group ONLY by filename, different views with same filename (unlikely?) would be grouped.
      // Let's stick to file_name as requested.
      // Group by file_name and keyence_device_name to separate same-named files on different devices
      const deviceKey = program.keyence_device_name || 'unknown';
      const key = `${program.file_name}::${deviceKey}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(program);
    });

    // For each group, sort by version descending
    // The first item is the "latest", the rest are "history"
    return Object.values(groups).map((group) => {
      const sorted = group.sort((a, b) => (b.version ?? 1) - (a.version ?? 1));
      const latest = sorted[0];
      const history = sorted.slice(1);

      // Attach history to the latest object for nested row rendering if needed,
      // or we can just use the `history` field if we transform the data structure.
      // Ideally, we return the 'latest' object with a special property 'history'.
      return {
        ...latest,
        history,
        key: latest.id // Ensure unique key
      };
    });
  }, [programsData]);


  const [syncProgram] = useSyncKeyenceProgramMutation();
  const [syncFromKeyence, { isLoading: isSyncingFromKeyence }] = useSyncFromKeyenceMutation();
  const [pushToDevice] = usePushKeyenceProgramToDeviceMutation();
  const [pushingId, setPushingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [validationProgram, setValidationProgram] = useState<KeyenceProgram | null>(null);
  const [historyGroupKey, setHistoryGroupKey] = useState<string | null>(null);

  const closeValidation = () => {
    setValidationProgram(null);
    // A background check may have finished while the modal was open.
    refetch();
  };

  // A program can reach the ERP without reaching the Keyence — the uploader may
  // have been working through a proxy outage. Pushing is the deferred half.
  const handlePushToDevice = async (id: string) => {
    setPushingId(id);
    try {
      await pushToDevice(id).unwrap();
      message.success('Program sent to the Keyence');
      refetch();
    } catch (error: any) {
      message.error(error?.data?.detail || 'Could not send the program to the Keyence');
    } finally {
      setPushingId(null);
    }
  };

  const handleSyncFromKeyence = async () => {
    try {
      const result = await syncFromKeyence({ projectId }).unwrap();
      const count = Array.isArray(result) ? result.length : 0;
      message.success(`Synced ${count} program(s) from Keyence`);
      refetch();
    } catch (error: any) {
      const detail = error?.data?.detail || 'Failed to sync from Keyence';
      if (error?.status === 404) {
        message.info(detail);
      } else {
        message.error(detail);
      }
    }
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      const result = await syncProgram(id).unwrap();
      if (result.detail === "Program is up to date.") {
        message.info("Program is up to date");
      } else {
        message.success("Program synced successfully");
        // The query might auto-update if tags were set, but for now we rely on refetch from parent queries if applicable,
        // or we can manually refetch. Warning: groupedData depends on programsData.
        // Let's assume tags are handled or we need to trigger refetch.
        // 'programsData' comes from 'useGetKeyenceProgramsByProjectQuery'.
        // I'll add refetch call if I can access it. Yes, lines 21.
        // But I am inside `columns`. I'll define `columns` inside component body (it is).
        refetch();
      }
    } catch (error: any) {
      if (error?.data?.detail) {
        message.error(`Sync failed: ${error.data.detail}`);
      } else {
        message.error("Sync failed");
      }
    } finally {
      setSyncingId(null);
    }
  };

  const columns = [
    {
      title: 'Program Name',
      dataIndex: 'file_name',
      key: 'file_name',
      width: '25%',
      render: (name: string) => <span style={{ wordBreak: 'break-all' }}>{name}</span>,
    },
    {
      title: 'View',
      dataIndex: 'view',
      key: 'view',
      width: '10%',
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: '10%',
      render: (version: number | null, record: KeyenceProgram & { history?: KeyenceProgram[]; key?: string }) => {
        const previous = record.history?.length ?? 0;
        if (previous === 0) return <Tag color="blue">v{version ?? 1}</Tag>;
        const open = () => setHistoryGroupKey(record.key ?? record.id);
        return (
          <Tooltip title={`View ${previous} previous version${previous === 1 ? '' : 's'}`}>
            <Tag
              color="blue"
              icon={<HistoryOutlined />}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={open}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  open();
                }
              }}
            >
              v{version ?? 1}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a: KeyenceProgram, b: KeyenceProgram) => (a.version ?? 1) - (b.version ?? 1),
    },
    {
      title: 'Keyence Device',
      dataIndex: 'keyence_device_name',
      key: 'keyence_device_name',
      width: '15%',
      render: (deviceName: string) => deviceName ? <Tag color="geekblue">{deviceName}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploaded_by_name',
      key: 'uploaded_by_name',
      width: '15%',
      render: (name: string, record: KeyenceProgram) => name || (record.uploaded_by === null ? <Tag>System</Tag> : '-'),
    },
    {
      title: 'On Device',
      dataIndex: 'device_sync_status',
      key: 'device_sync_status',
      width: '10%',
      render: (status: string | undefined, record: KeyenceProgram) => {
        if (!status || status === 'Synced') return <Tag color="green">On Keyence</Tag>;

        // 'Pending' keeps retrying on its own; 'Failed' has spent its budget
        // and is waiting on a person.
        const retrying = status === 'Pending';
        const tag = (
          <Tag color={retrying ? 'orange' : 'red'}>
            {retrying ? 'Retrying' : 'Send failed'}
          </Tag>
        );

        const lines = [];
        if (retrying && record.device_sync_next_attempt_at) {
          lines.push(`Next attempt ${dayjs(record.device_sync_next_attempt_at).format('h:mm A')}`);
        }
        if (!retrying) {
          lines.push('Automatic retries gave up — use "Send to Keyence".');
        }
        if (record.device_sync_attempts) {
          lines.push(`${record.device_sync_attempts} failed attempt(s)`);
        }
        if (record.device_sync_error) lines.push(record.device_sync_error);

        return lines.length
          ? <Tooltip title={lines.join(' · ')}>{tag}</Tooltip>
          : tag;
      },
    },
    {
      title: 'Validation',
      dataIndex: 'validation',
      key: 'validation',
      width: '10%',
      render: (validation: KeyenceValidationSummary | null | undefined, record: KeyenceProgram) => {
        if (!validation) {
          return (
            <Tooltip title="Not validated: uploaded before automatic checking, or while it was turned off">
              <Text type="secondary">-</Text>
            </Tooltip>
          );
        }
        const indicator = VALIDATION_INDICATOR[validation.outcome];
        const flagged =
          validation.outcome === 'Failed'
            ? validation.error_count
            : validation.outcome === 'Warning'
              ? validation.warning_count
              : 0;
        const count = flagged ? ` (${flagged})` : '';
        return (
          <Tooltip title={validationTooltip(validation)}>
            <Button
              type="link"
              size="small"
              className="p-0"
              icon={<span style={{ color: indicator.color }}>{indicator.icon}</span>}
              onClick={() => setValidationProgram(record)}
              style={{ color: indicator.color }}
            >
              {indicator.label}{count}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: 'Date Uploaded',
      dataIndex: 'created_time',
      key: 'created_time',
      width: 190,
      render: (date: string) =>
        date ? (
          <span style={{ whiteSpace: 'nowrap' }}>{dayjs(date).format('YYYY-MM-DD h:mm A')}</span>
        ) : '',
      sorter: (a: KeyenceProgram, b: KeyenceProgram) => dayjs(a.created_time).unix() - dayjs(b.created_time).unix(),
    },
    {
      title: '',
      key: 'action',
      width: '10%',
      align: 'center' as const,
      render: (_: unknown, record: KeyenceProgram) => {
        const items: MenuProps['items'] = [
          {
            key: 'download',
            icon: <DownloadOutlined />,
            disabled: !record.sqfx_file,
            label: (
              <a href={record.sqfx_file} target="_blank" rel="noreferrer">
                Download
              </a>
            ),
          },
          {
            key: 'sync',
            icon: <SyncOutlined />,
            label: 'Check for updates from Keyence',
            onClick: () => handleSync(record.id),
          },
        ];
        if (record.device_sync_status && record.device_sync_status !== 'Synced') {
          items.push({
            key: 'push',
            icon: <CloudUploadOutlined />,
            label: 'Send to Keyence',
            onClick: () => handlePushToDevice(record.id),
          });
        }
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button loading={pushingId === record.id || syncingId === record.id}>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    }
  ];

  // The group is looked up by key rather than held as a snapshot, so a refetch
  // (after a sync or push from inside the modal) shows up in the open modal.
  const historyGroup = groupedData.find((group) => group.key === historyGroupKey) ?? null;
  // Every version in a group shares its file name and device; the title says them once.
  // The main table's percentage widths assume all of its columns; with two
  // dropped they no longer add up, and the slack lands on the fixed date column.
  const historyColumns = columns
    .filter((column) => column.key !== 'file_name' && column.key !== 'keyence_device_name')
    .map((column) => ({ ...column, width: HISTORY_COLUMN_WIDTHS[column.key as string] ?? column.width }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>Uploaded Keyence Programs</Title>
        {programsData && programsData.length === 0 && (
          <Button
            type="primary"
            icon={<SyncOutlined spin={isSyncingFromKeyence} />}
            onClick={handleSyncFromKeyence}
            loading={isSyncingFromKeyence}
          >
            Sync from Keyence
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={groupedData}
        loading={isLoading}
        pagination={false}
        rowKey="key"
        scroll={{ x: TABLE_MIN_WIDTH }}
      />
      <Modal
        title={
          historyGroup && (
            <div className="flex flex-wrap items-center gap-2 pr-6">
              <span>Previous versions</span>
              <Text type="secondary" style={{ wordBreak: 'break-all' }}>
                {historyGroup.file_name}
              </Text>
              {historyGroup.keyence_device_name && (
                <Tag color="geekblue" className="m-0">{historyGroup.keyence_device_name}</Tag>
              )}
            </div>
          )
        }
        open={historyGroup !== null}
        onCancel={() => setHistoryGroupKey(null)}
        width={1000}
        centered
        destroyOnClose
        footer={[
          <Button key="close" type="primary" onClick={() => setHistoryGroupKey(null)}>
            Close
          </Button>,
        ]}
      >
        <Table
          columns={historyColumns}
          dataSource={historyGroup?.history ?? []}
          pagination={false}
          rowKey="id"
          scroll={{ x: HISTORY_TABLE_MIN_WIDTH, y: 480 }}
        />
      </Modal>
      <Modal
        title={
          validationProgram
            ? `Keyence Program Validation — v${validationProgram.version ?? 1}`
            : 'Keyence Program Validation'
        }
        open={validationProgram !== null}
        onCancel={closeValidation}
        width={900}
        centered
        destroyOnClose
        footer={[
          <Button key="close" type="primary" onClick={closeValidation}>
            Close
          </Button>,
        ]}
      >
        {validationProgram && (
          <UploadValidationResults
            showUploadSummary={false}
            results={[
              {
                key: validationProgram.id,
                fileName: validationProgram.file_name,
                view: validationProgram.view,
                deviceName: validationProgram.keyence_device_name,
                validation: validationProgram.validation ?? null,
              },
            ]}
          />
        )}
      </Modal>
    </>
  );
};

export default KeyencePrograms;
